import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Check, X } from 'lucide-react';
import { useEmbeddingsProviders } from '@/hooks/useEmbeddingsProviders';
import { useToast } from '@/hooks/use-toast';
import {
  CUSTOM_PROVIDER_NOTE, DIMENSION_NOTE, EMBEDDINGS_PRESETS, OLLAMA_EMBEDDING_MODELS, OLLAMA_HELP, OPENAI_EMBEDDING_MODELS, chatOnlyHostError, isLocalBaseUrl, KEY_REENTRY_HELP, REEMBED_WARNING, canSave, canTest, describeApiError,
  describeTest, requiresKeyReentry, sameConfig,
  type EmbeddingsPresetKey, type EmbeddingsProvider, type EmbeddingsTestResult, type TestedConfig,
} from '@/lib/embeddingsProviderLogic';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  provider?: EmbeddingsProvider;
  /** Hook actions are shared with the parent list so both stay in sync. */
  actions: Pick<ReturnType<typeof useEmbeddingsProviders>, 'testProvider' | 'saveProvider'>;
}

export function EmbeddingsProviderDialog({ open, onOpenChange, provider, actions }: Props) {
  const { toast } = useToast();
  const [label, setLabel] = useState('');
  const [preset, setPreset] = useState<EmbeddingsPresetKey>('openai');
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('');
  const [test, setTest] = useState<{ result: EmbeddingsTestResult; config: TestedConfig } | null>(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setApiKey('');
      setTest(null);
      setTesting(false);
      setSaving(false);
      setFormError(null);
      return;
    }
    const p: EmbeddingsPresetKey = !provider ? 'openai'
      : provider.base_url === EMBEDDINGS_PRESETS.openai.baseUrl ? 'openai'
      : isLocalBaseUrl(provider.base_url) ? 'ollama' : 'custom';
    setPreset(p);
    setOllamaOther(false);
    setLabel(provider?.label ?? '');
    setBaseUrl(provider?.base_url ?? EMBEDDINGS_PRESETS.openai.baseUrl);
    setModel(provider?.default_model ?? EMBEDDINGS_PRESETS.openai.model);
    if (p === 'openai') setBaseUrl(EMBEDDINGS_PRESETS.openai.baseUrl);
    setApiKey('');
    setTest(null);
    setTesting(false);
    setSaving(false);
    setFormError(null);
  }, [open, provider]);

  const current: TestedConfig = { baseUrl, model, apiKey };
  const isEdit = !!provider;
  const isOpenAi = preset === 'openai';
  const isOllama = preset === 'ollama';
  const keyless = EMBEDDINGS_PRESETS[preset].keyless;
  const hostError = chatOnlyHostError(baseUrl);
  const modelOptions = OPENAI_EMBEDDING_MODELS.some((m) => m.id === model)
    ? OPENAI_EMBEDDING_MODELS : [...OPENAI_EMBEDDING_MODELS, { id: model, label: model, note: 'current' }];
  const OTHER = '__other__';
  const ollamaKnown = OLLAMA_EMBEDDING_MODELS.some((m) => m.id === model);
  const [ollamaOther, setOllamaOther] = useState(false);
  const ollamaSelectValue = ollamaOther || !ollamaKnown ? OTHER : model;

  const handlePreset = (v: string) => {
    const key = v as EmbeddingsPresetKey;
    setPreset(key);
    setOllamaOther(false);
    setBaseUrl(EMBEDDINGS_PRESETS[key].baseUrl);
    setModel(EMBEDDINGS_PRESETS[key].model);
    setTest(null);
  };

  const handleTest = async () => {
    setTesting(true);
    setFormError(null);
    const config = { ...current };
    try {
      const result = await actions.testProvider({
        id: provider?.id, baseUrl: baseUrl.trim(), apiKey: apiKey || undefined, defaultModel: model.trim(),
      });
      setTest({ result, config });
    } catch (e) {
      setTest(null);
      setFormError((e as Error).message);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!test?.result.testToken) return;
    setSaving(true);
    setFormError(null);
    try {
      const r = await actions.saveProvider({
        id: provider?.id, label: label.trim(), preset, baseUrl: baseUrl.trim(), apiKey: apiKey || undefined,
        defaultModel: model.trim(), testToken: test.result.testToken, dimension: test.result.dimension ?? 0,
      });
      toast({
        title: provider ? `Updated ${label}` : `Added ${label}`,
        description: r.reembedRequired ? REEMBED_WARNING : undefined,
      });
      setApiKey('');
      setTest(null);
      onOpenChange(false);
    } catch (e) {
      const err = e as Error & { code?: string };
      setFormError(describeApiError(err.code, err.message));
    } finally {
      setSaving(false);
    }
  };

  const shown = test ? describeTest(test.result) : null;
  const dirty = !!test && !sameConfig(test.config, current);
  const storedHasKey = !!provider?.api_key_last4;
  const needsKey = requiresKeyReentry({ keyless, isEdit, baseUrl, storedBaseUrl: provider?.base_url, apiKey, storedHasKey });
  const testable = canTest({ keyless, baseUrl, model, apiKey, isEdit, storedBaseUrl: provider?.base_url, storedHasKey });
  const handleOpenChange = (o: boolean) => {
    if (!o && (saving || testing)) return;
    onOpenChange(o);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit embeddings provider' : 'Add embeddings provider'}</DialogTitle>
          <DialogDescription>
            Configure an OpenAI-compatible embeddings endpoint (hosted, or a local Ollama). Test it before saving.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">{DIMENSION_NOTE}</p>

          <div className="space-y-1.5">
            <Label htmlFor="emb-label">Label</Label>
            <Input id="emb-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. OpenAI embeddings" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="emb-preset">Preset</Label>
            <Select value={preset} onValueChange={handlePreset}>
              <SelectTrigger id="emb-preset"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(EMBEDDINGS_PRESETS) as EmbeddingsPresetKey[]).map((k) => (
                  <SelectItem key={k} value={k}>{EMBEDDINGS_PRESETS[k].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="emb-base-url">Base URL</Label>
            <Input id="emb-base-url" value={baseUrl} onChange={(e) => { setBaseUrl(e.target.value.trim()); setTest(null); }}
              readOnly={isOpenAi} aria-readonly={isOpenAi} className={isOpenAi ? 'bg-muted/50' : undefined}
              aria-invalid={!!hostError} aria-describedby={hostError ? 'emb-host-error' : undefined}
              placeholder={isOllama ? 'http://host.docker.internal:11434/v1' : 'https://api.example.com/v1'} />
            {hostError && <p id="emb-host-error" role="alert" className="text-xs text-destructive">{hostError}</p>}
            {isOllama && <p className="text-xs text-muted-foreground">{OLLAMA_HELP}</p>}
            {!isOpenAi && !isOllama && <p className="text-xs text-muted-foreground">{CUSTOM_PROVIDER_NOTE}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="emb-model">Embedding model</Label>
            {isOpenAi ? (
              <Select value={model} onValueChange={(v) => { setModel(v); setTest(null); }}>
                <SelectTrigger id="emb-model"><SelectValue placeholder="Select a model" /></SelectTrigger>
                <SelectContent>
                  {modelOptions.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      <span className="font-medium">{m.label}</span>
                      <span className="ms-2 text-xs text-muted-foreground">{m.note}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : isOllama ? (
              <div className="space-y-2">
                <Select value={ollamaSelectValue} onValueChange={(v) => {
                  if (v === OTHER) { setOllamaOther(true); setModel(ollamaKnown ? '' : model); } else { setOllamaOther(false); setModel(v); }
                  setTest(null);
                }}>
                  <SelectTrigger id="emb-model"><SelectValue placeholder="Select a model" /></SelectTrigger>
                  <SelectContent>
                    {OLLAMA_EMBEDDING_MODELS.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        <span className="font-medium">{m.label}</span>
                        <span className="ms-2 text-xs text-muted-foreground">{m.note}</span>
                      </SelectItem>
                    ))}
                    <SelectItem value={OTHER}>Other…</SelectItem>
                  </SelectContent>
                </Select>
                {ollamaSelectValue === OTHER && (
                  <Input aria-label="Other embedding model name" value={model} onChange={(e) => { setModel(e.target.value); setTest(null); }}
                    placeholder="model name, e.g. all-minilm" />
                )}
              </div>
            ) : (
              <Input id="emb-model" value={model} onChange={(e) => { setModel(e.target.value); setTest(null); }}
                placeholder="text-embedding-3-small" />
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="emb-api-key">API key{keyless ? ' (not required)' : ''}</Label>
            <Input id="emb-api-key" type="password" autoComplete="new-password" value={apiKey}
              onChange={(e) => { setApiKey(e.target.value); setTest(null); }}
              placeholder={provider?.api_key_last4 ? `•••• ${provider.api_key_last4}` : keyless ? 'leave empty for a local endpoint' : 'sk-...'}
              aria-describedby="emb-api-key-help" />
            <p id="emb-api-key-help" className="text-xs text-muted-foreground">
              {isEdit ? 'Leave blank to keep the current key. The key is write-only and is never shown again.' : 'Stored encrypted; never shown again after saving.'}
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Button type="button" variant="outline" onClick={handleTest} disabled={testing || saving || !testable}
                aria-describedby={needsKey ? 'emb-key-reentry' : undefined}>
                {testing ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
                Test connection
              </Button>
            </div>
            {needsKey && <p id="emb-key-reentry" className="text-xs text-muted-foreground">{KEY_REENTRY_HELP}</p>}
            <div aria-live="polite" className="min-h-5 text-sm">
              {shown && (
                <div className={shown.ok ? 'text-success' : 'text-destructive'}>
                  <span className="flex items-center gap-1 font-medium">
                    {shown.ok ? <Check className="h-4 w-4" aria-hidden /> : <X className="h-4 w-4" aria-hidden />}
                    {shown.title}
                  </span>
                  {shown.detail && <span className="block text-xs">{shown.detail}</span>}
                  {shown.raw && (
                    <details className="mt-1 text-xs text-muted-foreground">
                      <summary className="cursor-pointer">Details</summary>
                      <pre className="mt-1 whitespace-pre-wrap break-all font-mono">{shown.raw}</pre>
                    </details>
                  )}
                </div>
              )}
              {dirty && (
                <span className="block text-xs text-muted-foreground">Settings changed since the last test. Test again to enable Save.</span>
              )}
              {formError && <span role="alert" className="block text-destructive">{formError}</span>}
            </div>
          </div>

          {isEdit && provider?.is_active && (
            <p className="rounded-md border border-warning/50 p-3 text-xs">
              This provider is active. Changing its URL or model takes effect within about a minute. {REEMBED_WARNING}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving || testing}>Cancel</Button>
          <Button type="button" onClick={handleSave}
            disabled={!canSave({ label, test, current, saving, testing, isEdit, storedBaseUrl: provider?.base_url, keyless, storedHasKey })}
            aria-describedby={needsKey ? 'emb-key-reentry' : undefined}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
